/**
 * The slot collector (1.1 deliverable 20). Each web module that implements a header pill or a
 * right-rail tab registers it in its own `slots.ts`; `SLOT_MODULES` gathers those files by
 * directory (`web/<module>`), and `composeSlots` registers them in a `SlotRegistry`, then the
 * caller's `extraModules` (1.E-1's `EDITOR_SLOT_MODULES`, keyed `editor/<part>`), without either
 * owner editing this file. 1.X's document page composes them in its stage 1a. `AppSlotOutlet` draws
 * an app slot (CS r4a deliverable 109): the sidebar's Workspace group, the Settings page's
 * sections and the catalogue's toolbar each render one, and since CS r5a (deliverable 148) the top
 * of the sidebar and Home's sections. `DocSlotOutlet` draws a document slot outside the frame's
 * pills and tabs: the header's mode switch and the document page's tabs (CS r5a).
 */
import {
  type AppSlotId,
  createSlotRegistry,
  type DocSlotId,
  SLOTS,
  type SlotRegistration,
  type SlotRegistry,
} from '@codex/contracts';
import { createElement, Fragment, type ReactNode, useState } from 'react';
import * as after from '../after/slots.js';
import * as bootstrap from '../bootstrap/slots.js';
import { SlotBoundary } from '../documents/slot-boundary.js';
import * as documents from '../documents/slots.js';
import * as foundry from '../foundry/slots.js';
import * as history from '../history/slots.js';
import * as importSlots from '../import/slots.js';
import * as ledger from '../ledger/slots.js';
import * as links from '../links/slots.js';
import * as reference from '../reference/slots.js';
import * as run from '../run/slots.js';
import * as sessions from '../sessions/slots.js';
import * as sheetForm from '../sheet-form/slots.js';
import * as sheetsCards from '../sheets/cards/slots.js';
import * as sheetsPc from '../sheets/pc/slots.js';
import * as types from '../types/slots.js';
import * as views from '../views/slots.js';
import * as when from '../when/slots.js';

/** Registrations by the directory that implements them, as the slot rows name it. */
export type SlotModules = Readonly<Record<string, readonly SlotRegistration[]>>;

/** Every web module's registrations, keyed `web/<module>`. */
export const SLOT_MODULES: SlotModules = {
  'web/types': types.slotRegistrations,
  'web/documents': documents.slotRegistrations,
  'web/ledger': ledger.slotRegistrations,
  'web/links': links.slotRegistrations,
  'web/when': when.slotRegistrations,
  'web/history': history.slotRegistrations,
  'web/reference': reference.slotRegistrations,
  'web/sheet-form': sheetForm.slotRegistrations,
  // CS r4a: the import's sidebar entry, Settings button and Tidy pill (2.B-2).
  'web/import': importSlots.slotRegistrations,
  // CS r5a: Tonight in the sidebar and on Home (2.D), the Run switch (2.E), the After tab (2.F).
  'web/sessions': sessions.slotRegistrations,
  'web/run': run.slotRegistrations,
  'web/after': after.slotRegistrations,
  // CS r4b: the catalogue toolbar's Views controls (2.K).
  'web/views': views.slotRegistrations,
  // CS r5b: the bootstrap wizard's sidebar entry and Settings section (2.J).
  'web/bootstrap': bootstrap.slotRegistrations,
  // CS r6c: Phase 2c's Foundry entries and refresh pill (2.I-2), the PC sheet pill (2.H) and the
  // card pill (2.M), each empty with flag:sheets off.
  'web/foundry': foundry.slotRegistrations,
  'web/sheets/pc': sheetsPc.slotRegistrations,
  'web/sheets/cards': sheetsCards.slotRegistrations,
};

export class SlotCompositionError extends Error {
  override readonly name = 'SlotCompositionError';
}

/** True when a slot row of `directory` has this registration's slot, order and owner. */
function matchesRow(directory: string, entry: SlotRegistration): boolean {
  return SLOTS.some(
    (row) =>
      row.directory === directory &&
      row.slot === entry.slot &&
      row.order === entry.order &&
      row.owner === entry.owner,
  );
}

/**
 * Registers `SLOT_MODULES`, then `extraModules`, into `registry` and returns it. Refuses, before
 * registering anything, a directory present in both records and a registration that matches no
 * slot row of its own directory (whichever record it came from); the registry itself refuses an
 * unknown slot or owner and a taken order.
 */
export function composeSlots(
  registry: SlotRegistry = createSlotRegistry(),
  extraModules: SlotModules = {},
): SlotRegistry {
  const records = [SLOT_MODULES, extraModules];
  for (const directory of Object.keys(extraModules)) {
    if (Object.hasOwn(SLOT_MODULES, directory)) {
      throw new SlotCompositionError(`slot directory ${directory} is named in both records`);
    }
  }
  for (const record of records) {
    for (const [directory, entries] of Object.entries(record)) {
      for (const entry of entries) {
        if (!matchesRow(directory, entry)) {
          throw new SlotCompositionError(
            `${directory}: ${entry.owner}'s registration in ${entry.slot} at order ${entry.order} matches no slot row of that directory`,
          );
        }
      }
    }
  }
  for (const record of records) {
    for (const entries of Object.values(record)) {
      for (const entry of entries) registry.register(entry);
    }
  }
  return registry;
}

/** The registry once per mount; null when the slots could not be composed. */
function useComposedSlots(registry: SlotRegistry | undefined): SlotRegistry | null {
  const [built] = useState<SlotRegistry | null>(() => {
    if (registry) return registry;
    try {
      return composeSlots(createSlotRegistry());
    } catch (error) {
      if (error instanceof SlotCompositionError || error instanceof RangeError) return null;
      throw error;
    }
  });
  return built;
}

/**
 * Renders an app slot's registrations in order, each in its own `SlotBoundary` (a failed entry
 * renders nothing), with no wrapper of its own: nothing at all while the slot is empty.
 */
export function AppSlotOutlet({
  slot,
  registry,
}: {
  readonly slot: AppSlotId;
  readonly registry?: SlotRegistry;
}): ReactNode {
  const composed = useComposedSlots(registry);
  const entries = composed ? composed.list(slot) : [];
  if (entries.length === 0) return null;
  return createElement(
    Fragment,
    null,
    ...entries.map((entry) =>
      createElement(SlotBoundary, {
        key: entry.order,
        slotId: `${slot}:${entry.order}`,
        fallback: null,
        // biome-ignore lint/correctness/noChildrenProp: SlotBoundary's props type requires children
        children: createElement(entry.component, {}),
      }),
    ),
  );
}

/**
 * Renders a document slot's registrations for `docId` in order, each in its own `SlotBoundary` (a
 * failed entry renders nothing), with no wrapper of its own: nothing at all while the slot is empty
 * (CS r5a deliverable 148: `header.modes` in the header, `page.tabs` on the document page). Each
 * entry decides for itself whether it shows (Run and After only on a session, with `sessions` on).
 */
export function DocSlotOutlet({
  slot,
  docId,
  registry,
}: {
  readonly slot: DocSlotId;
  readonly docId: string;
  readonly registry?: SlotRegistry;
}): ReactNode {
  const composed = useComposedSlots(registry);
  const entries = composed ? composed.list(slot) : [];
  if (entries.length === 0) return null;
  return createElement(
    Fragment,
    null,
    ...entries.map((entry) =>
      createElement(SlotBoundary, {
        key: entry.order,
        slotId: `${slot}:${entry.order}`,
        fallback: null,
        // biome-ignore lint/correctness/noChildrenProp: SlotBoundary's props type requires children
        children: createElement(entry.component, { docId }),
      }),
    ),
  );
}
